import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Trash2 } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import type { AccessList } from '@/lib/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function AccessManager({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const access = useQuery({ queryKey: ['access'], queryFn: api.getAccess, enabled: open })

  const [emails, setEmails] = useState<string[]>([])
  const [newEmail, setNewEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<{ kind: 'idle' | 'ok' | 'err'; message?: string }>({
    kind: 'idle',
  })

  useEffect(() => {
    if (access.data) setEmails(access.data.emails)
  }, [access.data])

  const roots = access.data?.rootEmails ?? []
  const dirty = useMemo(() => {
    if (!access.data) return false
    return JSON.stringify([...access.data.emails].sort()) !== JSON.stringify([...emails].sort())
  }, [access.data, emails])

  const save = useMutation({
    mutationFn: async () => await api.putAccess(emails),
    onSuccess: (next: AccessList) => {
      queryClient.setQueryData(['access'], next)
      setEmails(next.emails)
      setStatus({ kind: 'ok', message: `Zapisano. Dodatkowych maili: ${next.emails.length}.` })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        setStatus({ kind: 'err', message: 'Walidacja nieudana — sprawdź format adresów.' })
        return
      }
      setStatus({ kind: 'err', message: (err as Error).message })
    },
  })

  function addEmail() {
    const candidate = newEmail.trim().toLowerCase()
    setStatus({ kind: 'idle' })
    if (!candidate) return
    if (!EMAIL_RE.test(candidate)) {
      setError('Nieprawidłowy adres e-mail.')
      return
    }
    if (roots.map((e) => e.toLowerCase()).includes(candidate)) {
      setError('Ten adres jest już na liście root (z konfiguracji).')
      return
    }
    if (emails.includes(candidate)) {
      setError('Ten adres jest już na liście.')
      return
    }
    setEmails((prev) => [...prev, candidate])
    setNewEmail('')
    setError(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Dostępy do CRM</DialogTitle>
          <DialogDescription>
            Lista maili, które mogą logować się do panelu.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
          {access.isLoading && <p className="text-sm text-muted-foreground">Ładowanie listy…</p>}
          {access.isError && (
            <p className="text-sm text-destructive">Błąd: {String(access.error)}</p>
          )}

          {access.data && (
            <>
              <div className="space-y-2">
                <Label htmlFor="new-email">Dodaj adres e-mail</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value)
                      if (error) setError(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addEmail()
                      }
                    }}
                    placeholder="nowy@firma.pl"
                  />
                  <Button type="button" onClick={addEmail}>
                    Dodaj
                  </Button>
                </div>
                {error && <p className="text-xs text-destructive">{error}</p>}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Dodatkowe maile ({emails.length})
                </p>
                {emails.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Brak dodatkowych adresów.</p>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {emails.map((email) => (
                      <li
                        key={email}
                        className="flex items-center justify-between px-3 py-2 text-sm"
                      >
                        <span className="truncate">{email}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setEmails((prev) => prev.filter((e) => e !== email))}
                        >
                          <Trash2 className="size-4" />
                          Usuń
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {roots.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Maile root ({roots.length}) — z konfiguracji, nieusuwalne
                  </p>
                  <ul className="divide-y rounded-md border bg-muted/40">
                    {roots.map((email) => (
                      <li
                        key={email}
                        className="flex items-center justify-between px-3 py-2 text-sm text-muted-foreground"
                      >
                        <span className="truncate">{email}</span>
                        <span className="ml-3 flex items-center gap-1 text-xs">
                          <Lock className="size-3" /> root
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <Separator />
        <div className="flex items-center justify-between gap-3">
          <span
            className={
              'text-xs ' +
              (status.kind === 'ok'
                ? 'text-emerald-600'
                : status.kind === 'err'
                  ? 'text-destructive'
                  : 'text-muted-foreground')
            }
          >
            {status.message ?? (dirty ? 'Niezapisane zmiany' : 'Brak zmian')}
          </span>
          <Button
            type="button"
            disabled={!dirty || save.isPending || !access.data}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
